/**
 * OneBase Dashboard
 * Panel principal del ERP OneBase con resumen de proyectos, presupuestos y órdenes
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { proyectoService, presupuestoVentaService } from '../services/onebaseService';
import { exportElementToPDF, formatCurrencyForPDF } from '../utils/pdfExport';
import { toast } from 'react-toastify';
import StockValidacionWidget from '../components/StockValidacionWidget';
import Loading from '../components/Loading';
import '../styles/onebase-dashboard.css';

// Mapeo de estados a nombres legibles
const ESTADO_LABELS = {
  'nuevo': 'Nuevo',
  'presupuesto_venta_en_trabajo': 'Presupuesto en Trabajo',
  'presupuesto_venta_en_aprobacion_interna': 'Presupuesto en Aprobación Interna',
  'presupuesto_venta_aprobado_internamente': 'Presupuesto Aprobado Internamente',
  'presupuesto_venta_enviado_a_cliente': 'Presupuesto Enviado a Cliente',
  'presupuesto_venta_aprobado_cliente': 'Presupuesto Aprobado por Cliente',
  'en_ejecucion': 'En Ejecución',
  'terminado': 'Terminado',
  'cancelado': 'Cancelado',
};

const OneBaseDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [stats, setStats] = useState({
    totalProyectos: 0,
    totalPresupuestos: 0,
    margenPromedio: 0,
  });
  const [proyectos, setProyectos] = useState([]);
  const [chartData, setChartData] = useState({
    estadoData: [],
    topProyectosData: [],
  });

  useEffect(() => {
    cargarDatos();
  }, []);

  const cargarDatos = async () => {
    setLoading(true);
    try {
      const [proyectosRes, presupuestosRes] = await Promise.all([
        proyectoService.getAll({ status: 'activo' }),
        presupuestoVentaService.getAll({ status: 'activo' }),
      ]);

      // Procesar respuestas - manejar diferentes formatos de API
      let proyectosData = Array.isArray(proyectosRes.data) ? proyectosRes.data : (proyectosRes.data.results || []);
      let presupuestosData = Array.isArray(presupuestosRes.data) ? presupuestosRes.data : (presupuestosRes.data.results || []);

      // Calcular margen promedio
      const margenPromedio = proyectosData.length > 0
        ? proyectosData.reduce((acc, p) => acc + parseFloat(p.margen_real || 0), 0) / proyectosData.length
        : 0;

      setStats({
        totalProyectos: proyectosData.length,
        totalPresupuestos: presupuestosData.length,
        margenPromedio: margenPromedio,
      });

      setProyectos(proyectosData.slice(0, 5));

      // Preparar datos para gráficos
      prepareChartData(proyectosData);
    } catch (error) {
      console.error('Error al cargar datos:', error);
      toast.error('Error al cargar dashboard');
    } finally {
      setLoading(false);
    }
  };

  const prepareChartData = (proyectosData) => {
    // Datos por estado
    const estadoCounts = {};
    proyectosData.forEach((p) => {
      estadoCounts[p.estado] = (estadoCounts[p.estado] || 0) + 1;
    });

    const estadoData = Object.entries(estadoCounts).map(([estado, count]) => ({
      name: ESTADO_LABELS[estado] || estado.charAt(0).toUpperCase() + estado.slice(1),
      value: count,
    }));

    // Top 5 proyectos por venta
    const topProyectosData = proyectosData
      .sort((a, b) => (b.total_venta || 0) - (a.total_venta || 0))
      .slice(0, 5)
      .map((p) => ({
        name: p.nombre.substring(0, 15),
        venta: parseFloat(p.total_venta || 0),
        utilidad: parseFloat(p.utilidad || 0),
      }));

    setChartData({
      estadoData,
      topProyectosData,
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
    }).format(value);
  };

  const COLORS = ['#69B32D', '#102826', '#f59e0b', '#10b981', '#ef4444'];

  const handleExportPDF = async () => {
    setExporting(true);
    try {
      const dashboardElement = document.querySelector('.onebase-dashboard');
      if (dashboardElement) {
        const filename = `dashboard_${new Date().toISOString().split('T')[0]}.pdf`;
        await exportElementToPDF(dashboardElement, filename, {
          orientation: 'landscape',
          margin: 10,
        });
        toast.success('Dashboard exportado correctamente');
      }
    } catch (error) {
      console.error('Error al exportar PDF:', error);
      toast.error('Error al exportar dashboard');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="onebase-dashboard onebase-proyeccion-recursos">


      {/* Validación de Stock de Recursos */}
      <StockValidacionWidget />
    </div>
  );
};

export default OneBaseDashboard;
